import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api } from "@/api";
import { API_BASE_URL } from "@/config";
import {
  DEFAULT_BRAND,
  applyRuntimeBrand,
  type PlatformBrand,
} from "@/lib/brandRuntime";

// The API returns origin-relative asset URLs; in production the API is a
// different origin from the web app, so they must be prefixed with the API base
// (mirrors how tenant logos are resolved) or the logo + favicon 404.
const absUrl = (u: string | null): string | null =>
  u && u.startsWith("/") ? `${API_BASE_URL}${u}` : u;

// Supplies the platform brand to React components. Seeded with the built-in
// defaults so the very first render is correct (no flash) and an un-rebranded
// platform is unchanged; on boot it fetches the live brand once and, if any
// field is overridden, re-renders + applies the imperative side effects
// (favicon, theme colours). Read via usePlatformBrand().

const Ctx = createContext<PlatformBrand>(DEFAULT_BRAND);

export function usePlatformBrand(): PlatformBrand {
  return useContext(Ctx);
}

export function PlatformBrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrand] = useState<PlatformBrand>(DEFAULT_BRAND);

  useEffect(() => {
    let cancelled = false;
    api
      .getPlatformBrand()
      .then((b) => {
        if (cancelled) return;
        const merged: PlatformBrand = {
          ...DEFAULT_BRAND,
          ...b,
          logo_url: absUrl(b.logo_url),
          icon_url: absUrl(b.icon_url),
          cert_logo_url: absUrl(b.cert_logo_url),
        };
        setBrand(merged);
        applyRuntimeBrand(merged);
      })
      .catch(() => {
        /* best-effort — keep the built-in defaults if the fetch fails */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <Ctx.Provider value={brand}>{children}</Ctx.Provider>;
}
